import { Injectable, inject } from '@angular/core';
import { DateService } from '../../../services/date/date.service';
import { TransferService } from '../transfer.service';
import { Currency } from '../../../domain/market.domain';
import { Account, AccountType, RapCodeType } from '../../../domain/account.domain';
import { OfiTransfer } from '../ofi/ofi-transfers.domain';
import { DataAnalyticsTaggingService } from '../../../features/data-analytics-tagging/data-analytics-tagging.service';
import { DataAnalyticsTaggingConstants } from '../../../features/data-analytics-tagging/data-analytics-tagging.constants';
import { CashTransfer, Day, FrequencyTypes } from './cash-transfer.domain';

@Injectable()
export class CashTransferService {
  private readonly dataAnalyticsTaggingService = inject(DataAnalyticsTaggingService);
  private readonly transferService = inject(TransferService);

  private static readonly SUPPORTED_WMM_ERROR_CODES: Array<string> = ['WMM_API_E2001', 'WMM_API_E6219', 'WMM_API_E6218',
    'WMM_API_E2304', 'WMM_API_E2307', 'WMM_API_E2317', 'WMM_API_E2309', 'WMM_API_E2314', 'WMM_API_E2008', 'WMM_API_E5014',
    'WMM_API_E5031', 'WMM_API_E5032', 'WMM_API_E5015', 'WMM_API_E5017', 'WMM_API_E5016', 'WMM_API_E5028', 'WMM_API_E5019',
    'WMM_API_E7003', 'WMM_API_E5035', 'WMM_API_E5036', 'WMM_API_E5037', 'WMM_API_E5021', 'WMM_API_E5025', 'WMM_API_E6225',
    'WMM_API_E6244', 'WMM_API_E5013', 'WMM_API_E2306', 'WMM_API_E5038', 'WMM_API_E5040'];

  getThirdBusinessDay(): Date {
    const today = new Date();
    if (today.getDay() < 3) {
      return DateService.getDateFrom(today, 3);
    } else if (today.getDay() === 6) {
      return DateService.getDateFrom(today, 4);
    } else {
      return DateService.getDateFrom(today, 5);
    }
  }

  getMaxDate(frequencyType: FrequencyTypes): Date {
    if (frequencyType === FrequencyTypes.BI_WEEKLY) {
      return DateService.getDateFrom(new Date(), 14);
    } else if (frequencyType === FrequencyTypes.MONTHLY) {
      return DateService.getDateFrom(new Date(), 30);
    } else {
      return undefined;
    }
  }

  getSemiMonthlyStartDate(): Date {
    let startDate = DateService.getDateFrom(new Date(), 3);
    const dayOfMonth = startDate.getDate();
    if (dayOfMonth !== 1 && dayOfMonth !== 15) {
      if (dayOfMonth < 15) {
        startDate = new Date(startDate.getFullYear(), startDate.getMonth(), 15);
      } else {
        startDate = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 1);
      }
    }
    return startDate;
  }

  getWeeklyDates(): Array<Date> {
    let numOfDays = 1;
    const weeklyDates = [];
    while (weeklyDates.length < 5) {
      const tempDate = DateService.getDateFrom(new Date(), numOfDays);
      if (!DateService.isWeekend(tempDate)) {
        weeklyDates.push(tempDate);
      }
      numOfDays++;
    }
    return weeklyDates;
  }

  getSelectedDay(cashTransferModel: CashTransfer): Day {
    return Object.values(Day)[cashTransferModel.startDate.getDay() - 1]
  }

  isValidDate(frequencyType: FrequencyTypes, startDate: Date, minDate: Date, maxDate: Date): boolean {
    return ((frequencyType === FrequencyTypes.BI_WEEKLY && !DateService.isWeekend(startDate))
      || frequencyType === FrequencyTypes.MONTHLY)
      && DateService.isDate(startDate)
      && startDate.setHours(0, 0, 0, 0) >= minDate.setHours(0, 0, 0, 0)
      && startDate.setHours(0, 0, 0, 0) <= maxDate.setHours(0, 0, 0, 0);
  }

  isTIKTransfer(cashTransferModel: CashTransfer): boolean {
    return this.isTIKTfsaTransfer(cashTransferModel) || this.isTIKRrspTransfer(cashTransferModel);
  }

  /**
   * This method is used for a specific case where the user has selected a Retail/TDCT HISA TFSA
   * as the From account and a DI TFSA as the To account. This type of transfer will require a ticket
   * to be created on the TIK system to be manually processed by the back office.
   */
  isTIKTfsaTransfer(cashTransferModel: CashTransfer): boolean {
    return cashTransferModel.selectedFromAccount?.tdctTfsaHisaAccount
      && cashTransferModel.selectedToAccount?.rapCode?.acctType === AccountType.TFSA
      && cashTransferModel.selectedToAccount?.currency === Currency.CAD;
  }

  /**
   * This method is used for a specific case where the user has selected a Retail/TDCT DISA RRSP
   * as the From account and a DI RRSP as the To account. This type of transfer will require a ticket
   * to be created on the TIK system to be manually processed by the back office.
   */
  isTIKRrspTransfer(cashTransferModel: CashTransfer): boolean {
    return cashTransferModel.selectedFromAccount?.tdctRrspDisaAccount
      && (cashTransferModel.selectedToAccount?.rapCode?.acctType === AccountType.SDRSP ||
        cashTransferModel.selectedToAccount?.rapCode?.acctType === AccountType.BASIC_RSP)
      && cashTransferModel.selectedToAccount?.currency === Currency.CAD;
  }

  isToAccountResp(cashTransferModel: CashTransfer): boolean {
    return cashTransferModel.selectedToAccount?.rapCode?.acctType === AccountType.RESP;
  }

  isToAccountRrsp(cashTransferModel: CashTransfer): boolean {
    return cashTransferModel.selectedToAccount?.rapCode?.acctType === AccountType.SDRSP ||
      cashTransferModel.selectedToAccount?.rapCode?.acctType === AccountType.BASIC_RSP;
  }

  isToAccountTfsa(cashTransferModel: CashTransfer): boolean {
    return cashTransferModel.selectedToAccount?.rapCode?.acctType === AccountType.TFSA;
  }

  showTfsaMessage(cashTransferModel: CashTransfer): boolean {
    const fromAccountRapCodeChar = this.getFromRapCodeChar(cashTransferModel);
    const toAccountRapCodeChar = this.getToRapCodeChar(cashTransferModel);

    return (fromAccountRapCodeChar === 'J' || fromAccountRapCodeChar === 'K') &&
      (toAccountRapCodeChar !== 'J' && toAccountRapCodeChar !== 'K');
  }

  shouldValidateAmountAgainstCLBalance(cashTransferModel: CashTransfer): boolean {
    return this.isTIKTransfer(cashTransferModel) && typeof cashTransferModel?.selectedFromAccount?.cashBalance === 'number';
  }

  validateAmountAgainstCLBalance(cashTransferModel: CashTransfer): boolean {
    const cashBalance = cashTransferModel?.selectedFromAccount?.cashBalance as number;
    const amount = cashTransferModel?.transferAmount;
    return amount > cashBalance;
  }

  showFhsaMessage(cashTransferModel: CashTransfer): boolean {
    return this.getToRapCodeChar(cashTransferModel) === 'O';
  }

  showContribution(cashTransferModel: CashTransfer): boolean {
    const selectedFromAccount = cashTransferModel.selectedFromAccount;
    const selectedToAccount = cashTransferModel.selectedToAccount;

    const toAccountRapCode = (selectedToAccount !== undefined && selectedToAccount.rapCode !== undefined) ?
      selectedToAccount.rapCode.name : '';
    const fromAccountRapCode = (selectedFromAccount !== undefined && selectedFromAccount.rapCode !== undefined) ?
      selectedFromAccount.rapCode.name : '';

    if (fromAccountRapCode === RapCodeType.US_SDRSP && toAccountRapCode === RapCodeType.SDRSP || this.isTIKRrspTransfer(cashTransferModel)) {
      return false;
    }

    return toAccountRapCode === RapCodeType.BASIC_RSP || toAccountRapCode === RapCodeType.SDRSP || toAccountRapCode === RapCodeType.CPRSP;
  }

  private isJournalTransfer(cashTransferModel: CashTransfer): boolean {
    const isSameAccountType = cashTransferModel.selectedFromAccount?.rapCode?.acctType === cashTransferModel.selectedToAccount?.rapCode?.acctType;
    const rapCodeStringValues = [cashTransferModel.selectedFromAccount?.rapCode?.stringValue, cashTransferModel.selectedToAccount?.rapCode?.stringValue];

    if (isSameAccountType && cashTransferModel.selectedFromAccount?.rapCode?.acctType == 'TFSA') {
      return rapCodeStringValues.includes('J') && rapCodeStringValues.includes('K');
    } else if (isSameAccountType && cashTransferModel.selectedFromAccount?.rapCode?.acctType == 'SDRSP') {
      return rapCodeStringValues.includes('S') && rapCodeStringValues.includes('U');
    }

    return false;
  }

  showContributionDisclaimer(cashTransferModel: CashTransfer): boolean {
    const selectedFromAccount = cashTransferModel.selectedFromAccount;
    const selectedToAccount = cashTransferModel.selectedToAccount;

    const isContributionAccount = this.isToAccountTfsa(cashTransferModel) ||
      this.isToAccountRrsp(cashTransferModel) ||
      selectedToAccount.rapCode?.acctType === AccountType.CPRSP ||
      this.showFhsaMessage(cashTransferModel) &&
      !selectedToAccount.tdctRrspDisaAccount &&
      !selectedToAccount.tdctTfsaHisaAccount;

    const isValidFromAccount = !selectedFromAccount.tdctRrspDisaAccount && !selectedFromAccount.tdctTfsaHisaAccount;

    return isContributionAccount && isValidFromAccount && !this.isTIKTransfer(cashTransferModel) && !this.isJournalTransfer(cashTransferModel);
  }

  showContributionRespDisclaimer(cashTransferModel: CashTransfer): boolean {
    const selectedFromAccount = cashTransferModel.selectedFromAccount;
    const isValidFromAccount = !selectedFromAccount.tdctRrspDisaAccount && !selectedFromAccount.tdctTfsaHisaAccount;

    return this.isToAccountResp(cashTransferModel) && isValidFromAccount && !this.isTIKTransfer(cashTransferModel);
  }

  isRecurringOrTIKTransfer(cashTransferModel: CashTransfer): boolean {
    return cashTransferModel.isRecurringTransfer || this.isTIKTransfer(cashTransferModel);
  }

  checkFromAccountRecurringEligible(cashTransferModel: CashTransfer): boolean {
    if (cashTransferModel.selectedFromAccount?.currency === Currency.CAD
      && cashTransferModel.selectedFromAccount !== undefined
      && cashTransferModel.selectedFromAccount.transferFromTDCTAccount !== undefined) {
      return cashTransferModel.selectedFromAccount.transferFromTDCTAccount;
    } else {
      return false;
    }
  }

  checkToAccountRecurringEligible(cashTransferModel: CashTransfer): boolean {
    if (cashTransferModel.selectedToAccount?.currency === Currency.CAD
      && cashTransferModel.selectedToAccount !== undefined
      && cashTransferModel.selectedToAccount.transferToDIAccount !== undefined) {
      return cashTransferModel.selectedToAccount.transferToDIAccount;
    } else {
      return false;
    }
  }

  isAmountValueExceeded(cashTransferModel: CashTransfer): boolean {
    return cashTransferModel.transferAmount > this.getMaxAmount(cashTransferModel);
  }

  isWmmErrorCodeSupported(code: string): boolean {
    return CashTransferService.SUPPORTED_WMM_ERROR_CODES.includes(code);
  }

  getMaxAmount(transferModel: CashTransfer | OfiTransfer): number {
    switch (transferModel.frequencyType) {
      case FrequencyTypes.WEEKLY:
        return 1250;
      case FrequencyTypes.BI_WEEKLY:
      case FrequencyTypes.SEMI_MONTHLY:
        return 2500;
      case FrequencyTypes.MONTHLY:
        return 5000;
      default:
        return 999999999999.99;
    }
  }

  setETAnalyticsDataForAmountError(transferModel: CashTransfer): void {
    const maxAmountAllowedBasedUponFreq = this.getMaxAmount(transferModel);
    const amountSubmitted = transferModel.transferAmount;
    if (amountSubmitted !== undefined && transferModel.frequencyType !== undefined && amountSubmitted > maxAmountAllowedBasedUponFreq) {
      const message = `Maximum ${this.transferService.getFrequencyValueForTagging(transferModel.frequencyType)} amount is $${maxAmountAllowedBasedUponFreq}`
      const eventData = DataAnalyticsTaggingConstants.getErrorPageEventData(message, 'cash', 'et_modal-transfers-cash');
      this.dataAnalyticsTaggingService.emitTaggingEvent(eventData);
    }
  }

  private getFromRapCodeChar(cashTransferModel: CashTransfer): string {
    return this.getRapCodeFromAccount(cashTransferModel.selectedFromAccount)
  }

  private getToRapCodeChar(cashTransferModel: CashTransfer): string {
    return this.getRapCodeFromAccount(cashTransferModel.selectedToAccount)
  }

  private getRapCodeFromAccount(account: Account): string {
    return account !== undefined && account.rapCode !== undefined ? account.rapCode.stringValue : '';
  }

}
